import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { BrandLogo } from '../ui/BrandLogo';
import { APP_FONT_FAMILY, brandColors, brandCss } from '../../theme/appTheme';
import type { AuthPillar } from './authCoverContent';

/** Auth screens — same Inter as the rest of the app */
export const AUTH_FONT = APP_FONT_FAMILY;

export const authFont = {
  family: AUTH_FONT,
  heading: {
    fontFamily: AUTH_FONT,
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  body: {
    fontFamily: AUTH_FONT,
    fontWeight: 400,
  },
};

export interface AuthCoverConfig {
  badge?: string;
  headline?: string;
  headlineAccent?: string;
  intro?: string;
  trustItems?: string[];
  highlights?: unknown[];
  stats?: unknown[];
  testimonial?: { quote: string; author: string; role?: string };
  features?: unknown[];
  pillars?: AuthPillar[];
  tagline?: string;
  heroImage?: string;
}

interface AuthCoverLayoutProps {
  children: React.ReactNode;
  cover?: AuthCoverConfig;
  formMaxWidth?: number;
  scrollForm?: boolean;
  showFormLogo?: boolean;
}

const PILLAR_ICONS: Record<AuthPillar['icon'], React.ReactNode> = {
  secure: <ShieldOutlinedIcon sx={{ fontSize: 22 }} />,
  fast: <BoltOutlinedIcon sx={{ fontSize: 22 }} />,
  simple: <PersonOutlineOutlinedIcon sx={{ fontSize: 22 }} />,
};

/** Soft lilac brand panel + white form — shared by login / register / forgot / reset */
export const AuthCoverLayout: React.FC<AuthCoverLayoutProps> = ({
  children,
  cover,
  formMaxWidth = 420,
  scrollForm = false,
  showFormLogo = true,
}) => (
  <Box
    sx={{
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      fontFamily: authFont.family,
      bgcolor: '#fff',
    }}
  >
    {/* Left — light lavender story */}
    <Box
      sx={{
        flex: { md: '1 1 52%' },
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { xs: 3.5, sm: 5, md: 7 },
        py: { xs: 5, md: 6 },
        minHeight: { xs: 'auto', md: '100dvh' },
        bgcolor: '#f4f0ff',
        color: brandColors.textDark,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: 420,
          height: 420,
          borderRadius: '50%',
          left: -160,
          top: -120,
          background: 'radial-gradient(circle, rgba(124,58,237,0.16) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 380,
          height: 380,
          borderRadius: '50%',
          right: -120,
          bottom: -80,
          background: 'radial-gradient(circle, rgba(167,139,250,0.22) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
        {(cover?.headline || cover?.headlineAccent) && (
          <Typography
            component="h1"
            sx={{
              ...authFont.heading,
              fontSize: { xs: '1.85rem', sm: '2.15rem', md: '2.45rem' },
              lineHeight: 1.2,
              mb: 1.5,
              color: '#0f172a',
            }}
          >
            {cover?.headline}{' '}
            {cover?.headlineAccent && (
              <Box component="span" sx={{ color: brandCss.primary }}>
                {cover.headlineAccent}
              </Box>
            )}
          </Typography>
        )}

        {cover?.intro && (
          <Typography
            sx={{
              ...authFont.body,
              fontSize: { xs: 14.5, md: 15.5 },
              lineHeight: 1.65,
              color: '#64748b',
              maxWidth: 440,
              mb: 3.5,
            }}
          >
            {cover.intro}
          </Typography>
        )}

        {cover?.pillars && cover.pillars.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: { xs: 2, sm: 2.5 },
              mb: 4,
            }}
          >
            {cover.pillars.map((p) => (
              <Box key={p.title} sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    mx: { xs: 0, sm: 'auto' },
                    mb: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(124,58,237,0.12)',
                    color: brandCss.primary,
                  }}
                >
                  {PILLAR_ICONS[p.icon]}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: '#0f172a', mb: 0.35, fontFamily: authFont.family }}>
                  {p.title}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.45, fontFamily: authFont.family }}>
                  {p.description}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Dashboard / laptop illustration */}
        <AuthDashboardArt />
      </Box>
    </Box>

    {/* Right — white form */}
    <Box
      sx={{
        flex: { md: '1 1 48%' },
        maxWidth: { md: 560 },
        width: '100%',
        ml: { md: 'auto' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: scrollForm ? 'flex-start' : 'center',
        px: { xs: 3, sm: 5 },
        py: { xs: 4.5, md: 5 },
        bgcolor: '#ffffff',
        minHeight: { xs: 'auto', md: '100dvh' },
        overflowY: scrollForm ? 'auto' : undefined,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: formMaxWidth,
          p: 0,
          bgcolor: 'transparent',
          boxShadow: 'none',
          fontFamily: authFont.family,
        }}
      >
        {showFormLogo && (
          <Box sx={{ mb: 3.25 }}>
            <BrandLogo height={38} />
          </Box>
        )}
        {children}
      </Paper>
    </Box>
  </Box>
);

/** Lightweight CSS illustration — laptop + plant (matches marketing look) */
const AuthDashboardArt: React.FC = () => (
  <Box
    sx={{
      position: 'relative',
      mt: { xs: 1, md: 2 },
      maxWidth: 440,
      userSelect: 'none',
      pointerEvents: 'none',
    }}
  >
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 360,
        mx: 'auto',
        animation: 'authFloat 6s ease-in-out infinite',
        '@keyframes authFloat': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-8px)' },
        },
      }}
    >
      {/* Laptop base */}
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: '14px 14px 8px 8px',
          border: '1px solid #e9e2ff',
          boxShadow: '0 22px 50px rgba(98,33,214,0.14)',
          overflow: 'hidden',
          p: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
          {['#f87171', '#fbbf24', '#34d399'].map((c) => (
            <Box key={c} sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c }} />
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 1 }}>
          <Box sx={{ bgcolor: '#f8f5ff', borderRadius: 1.5, p: 1, minHeight: 88 }}>
            <Box sx={{ height: 6, width: '55%', bgcolor: '#ddd6fe', borderRadius: 1, mb: 1 }} />
            {[40, 65, 50, 80].map((h, i) => (
              <Box
                key={i}
                sx={{
                  display: 'inline-block',
                  width: 10,
                  height: h * 0.55,
                  mr: 0.6,
                  borderRadius: 0.75,
                  bgcolor: i % 2 ? brandCss.primary : '#a78bfa',
                  verticalAlign: 'bottom',
                }}
              />
            ))}
          </Box>
          <Box sx={{ bgcolor: '#f8f5ff', borderRadius: 1.5, p: 1, minHeight: 88 }}>
            <Box sx={{ height: 6, width: '40%', bgcolor: '#ddd6fe', borderRadius: 1, mb: 1.25 }} />
            <Box
              component="svg"
              viewBox="0 0 120 48"
              sx={{ width: '100%', height: 48, display: 'block' }}
            >
              <path
                d="M0 36 C20 34, 28 18, 45 22 S70 40, 90 18 S110 8, 120 14"
                fill="none"
                stroke={brandCss.primary}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <path
                d="M0 40 C22 38, 30 28, 48 30 S72 42, 92 28 S112 20, 120 24"
                fill="none"
                stroke="#c4b5fd"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          height: 10,
          mx: '8%',
          bgcolor: '#ede9fe',
          borderRadius: '0 0 10px 10px',
          border: '1px solid #e9e2ff',
          borderTop: 'none',
        }}
      />
      {/* Plant */}
      <Box
        sx={{
          position: 'absolute',
          right: { xs: -6, sm: -18 },
          bottom: 8,
          width: 54,
          height: 72,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 18,
            top: 0,
            width: 18,
            height: 36,
            borderRadius: '50% 50% 40% 40%',
            bgcolor: '#86efac',
            transform: 'rotate(-18deg)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: 28,
            top: 4,
            width: 16,
            height: 32,
            borderRadius: '50% 50% 40% 40%',
            bgcolor: '#4ade80',
            transform: 'rotate(16deg)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: 12,
            bottom: 0,
            width: 32,
            height: 22,
            borderRadius: '6px 6px 10px 10px',
            bgcolor: '#c4b5fd',
            boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.35)',
          }}
        />
      </Box>
    </Box>
  </Box>
);
